import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import { analyticsEvent } from 'global/helpers/analytics'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CreateOrEditBusinessEntityDialog from './CreateOrEditBusinessEntityDialog'
import { BUSINESS_ENTITIES_QUERY_KEY, deleteBusinessEntity } from './CarrierProfile.service'
import type { BusinessEntity } from './CarrierProfile.types'

interface BusinessEntitiesListProps {
  readonly businessEntities: BusinessEntity[]
}

const BusinessEntitiesList = ({
  businessEntities,
}: BusinessEntitiesListProps): ReactElement => {
  const { t } = useTranslation()
  const [activeBusinessEntity, setActiveBusinessEntity] = useState<BusinessEntity>()
  const queryClient = useQueryClient()
  const deleteBusinessEntityMutation = useMutation({
    mutationFn: deleteBusinessEntity,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BUSINESS_ENTITIES_QUERY_KEY] }),
  })
  const editBusinessEntityDialog = useDialog<typeof CreateOrEditBusinessEntityDialog>()

  const handleEditClick = useCallback((businessEntitiy: BusinessEntity) => {
    setActiveBusinessEntity(businessEntitiy)
    editBusinessEntityDialog.handleOpen()
  }, [])

  const handleDeleteBusinessEntityDialogAction = useCallback(async () => {
    if (!activeBusinessEntity) {
      return
    }
    await deleteBusinessEntityMutation.mutateAsync(activeBusinessEntity.id)
    analyticsEvent('polestar_carrier_profile_business_entity_removed')
  }, [activeBusinessEntity])

  const deleteBusinessEntityDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: handleDeleteBusinessEntityDialogAction,
  })

  const handleDeleteClick = useCallback((businessEntity: BusinessEntity) => {
    setActiveBusinessEntity(businessEntity)
    deleteBusinessEntityDialog.handleOpen()
  }, [])

  return (
    <>
      <Grid container spacing={2}>
        {businessEntities.map((businessEntity) => (
          <Grid key={businessEntity.id} size={3}>
            <Card variant="outlined" className="hoverable" sx={{ padding: 2 }}>
              <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="p2" color="primary.main">{businessEntity.location}</Typography>
                  <Typography color="primary.main">{t(`screens.cs.carrier_profile.types.${businessEntity.type}`)}</Typography>
                  <Typography color="primary.main">{businessEntity.registrationTaxNumber}</Typography>
                </Box>
                <Stack direction="row">
                  <IconButton
                    size="small"
                    className="hover-opacity"
                    sx={{ color: 'primary.main' }}
                    onClick={() => handleEditClick(businessEntity)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    className="hover-opacity"
                    sx={{ color: 'primary.main' }}
                    onClick={() => handleDeleteClick(businessEntity)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>
      {activeBusinessEntity && (
        <>
          <CreateOrEditBusinessEntityDialog
            payload={{
              businessEntity: activeBusinessEntity,
            }}
            open={editBusinessEntityDialog.isOpen}
            onAction={editBusinessEntityDialog.handleAction}
            onClose={editBusinessEntityDialog.handleClose}
          />
          <KNConfirmationDialog
            message={t('screens.cs.carrier_profile.business_entities.delete_confirmation')}
            continueLabel={t('general.delete')}
            open={deleteBusinessEntityDialog.isOpen}
            onAction={deleteBusinessEntityDialog.handleAction}
            onClose={deleteBusinessEntityDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default BusinessEntitiesList
